import { siteConfig } from "@/config/site";
import { landingContent } from "@/content/landing";
import { SourceLogoList, type SourceItem } from "@/components/ui/SourceLogoList";
import { featureFileHostingSources } from "@/lib/file-hosting-sources";

/** Inline HTML/CSS illustrations for FeatureBlock components */

const isScriptario = siteConfig.siteId === "scriptario";
const illustrationLocale = isScriptario && siteConfig.locale !== "ru" ? siteConfig.locale : "ru";

const illustrationCopy = {
  de: {
    reports: { add: "KI-Bericht hinzufügen", menu: ["Allgemein", "Meetings", "Inhalte", "Präsentationen", "Forschung", "Lehre", "IT und Produkt", "Vertrieb", "Recruiting"], cards: [{ title: "Meeting-Bericht", desc: "Formelle Meeting-Notizen" }, { title: "Aufgaben", desc: "Aufgaben und nächste Schritte" }] },
    chat: { assistant: "KI-Assistent", firstQuestion: "Worum geht es in diesem Video? Wie ist die Stimmung?", firstAnswer: "Das Video behandelt die Aktualisierung eines Unternehmensportals, sein Design, seine Struktur und den Einführungsplan.", secondQuestion: "Wie würden Sie das Video gliedern?", secondAnswer: "Das Gespräch behandelt die Aktualisierung des Portals, das Design und die Struktur...", input: "Schreiben..." },
    speakers: [
      { name: "Daniel", color: "rgba(227,134,116,0.4)", time: "00:10:42", text: "Ich schlage eine neue Kennzahl vor. Vergessen wir MQL und SQL. Messen wir die Zeit vom ersten Klick bis zum bezahlten Test." },
      { name: "Maya", color: "rgba(165,191,120,0.4)", time: "01:20:19", text: "Früher dauerte das 14 Tage. Im letzten Quartal waren es 5 Tage. Bis Jahresende wollen wir 2 Stunden erreichen." },
    ],
    assistant: { title: "Was möchten Sie aufnehmen?", next: "Weiter", recording: "Aufnahme läuft", pause: "Pause", stop: "Stoppen", options: [{ active: true, title: "Bildschirm + Mikrofon", desc: "Bildschirm und Stimme" }, { active: false, title: "Nur Mikrofon", desc: "Stimme ohne Video" }] },
  },
  en: {
    reports: {
      add: "Add AI report",
      menu: ["General", "Work meetings", "Content", "Presentations", "Research", "Teaching", "IT and product", "Sales", "Recruiting"],
      cards: [
        { title: "Meeting report", desc: "Formal meeting notes" },
        { title: "Action items", desc: "Tasks and next steps" },
      ],
    },
    chat: {
      assistant: "AI assistant",
      firstQuestion: "What is this video about? What is the mood?",
      firstAnswer: "The video discusses a company portal update, its design, structure, and rollout plan.",
      secondQuestion: "How would you split the video into sections?",
      secondAnswer: "The conversation covers the portal update, design, and structure...",
      input: "Write...",
    },
    speakers: [
      { name: "Daniel", color: "rgba(227,134,116,0.4)", time: "00:10:42", text: "I suggest a new metric. Forget MQL and SQL. Let us track trust velocity: time from first click to a paid trial." },
      { name: "Maya", color: "rgba(165,191,120,0.4)", time: "01:20:19", text: "It used to take 14 days. Last quarter it was 5 days. By year end, our goal is 2 hours." },
    ],
    assistant: {
      title: "What to record?",
      next: "Next",
      recording: "Recording",
      pause: "Pause",
      stop: "Stop",
      options: [
        { active: true, title: "Screen + mic", desc: "Screen and voice" },
        { active: false, title: "Mic only", desc: "Voice without video" },
      ],
    },
  },
  es: {
    reports: {
      add: "Añadir informe con IA",
      menu: ["General", "Reuniones de trabajo", "Contenido", "Presentaciones", "Investigación", "Docencia", "TI y producto", "Ventas", "Selección"],
      cards: [
        { title: "Informe de reunión", desc: "Notas formales de la reunión" },
        { title: "Tareas", desc: "Acciones y próximos pasos" },
      ],
    },
    chat: {
      assistant: "Asistente con IA",
      firstQuestion: "¿De qué trata este video? ¿Cuál es el tono?",
      firstAnswer: "El video trata sobre la actualización de un portal corporativo, su diseño, estructura y plan de lanzamiento.",
      secondQuestion: "¿Cómo dividirías el video en secciones?",
      secondAnswer: "La conversación cubre la actualización del portal, el diseño y la estructura...",
      input: "Escribe...",
    },
    speakers: [
      { name: "Daniel", color: "rgba(227,134,116,0.4)", time: "00:10:42", text: "Propongo una nueva métrica. Olvidemos MQL y SQL. Midamos la velocidad de confianza: del primer clic a la prueba de pago." },
      { name: "Maya", color: "rgba(165,191,120,0.4)", time: "01:20:19", text: "Antes tardábamos 14 días. El trimestre pasado fueron 5 días. Para fin de año, la meta es 2 horas." },
    ],
    assistant: {
      title: "¿Qué grabamos?",
      next: "Siguiente",
      recording: "Grabando",
      pause: "Pausa",
      stop: "Detener",
      options: [
        { active: true, title: "Pantalla + micrófono", desc: "Pantalla y voz" },
        { active: false, title: "Solo micrófono", desc: "Voz sin video" },
      ],
    },
  },
  "pt-br": {
    reports: {
      add: "Adicionar relatório com IA",
      menu: ["Geral", "Reuniões de trabalho", "Conteúdo", "Apresentações", "Pesquisa", "Ensino", "TI e produto", "Vendas", "Recrutamento"],
      cards: [
        { title: "Relatório da reunião", desc: "Notas formais da reunião" },
        { title: "Tarefas", desc: "Ações e próximos passos" },
      ],
    },
    chat: {
      assistant: "Assistente com IA",
      firstQuestion: "Sobre o que é este vídeo? Qual é o tom?",
      firstAnswer: "O vídeo discute a atualização de um portal corporativo, seu design, estrutura e plano de lançamento.",
      secondQuestion: "Como você dividiria o vídeo em seções?",
      secondAnswer: "A conversa cobre a atualização do portal, o design e a estrutura...",
      input: "Escreva...",
    },
    speakers: [
      { name: "Daniel", color: "rgba(227,134,116,0.4)", time: "00:10:42", text: "Sugiro uma nova métrica. Esqueça MQL e SQL. Vamos medir a velocidade de confiança: do primeiro clique ao teste pago." },
      { name: "Maya", color: "rgba(165,191,120,0.4)", time: "01:20:19", text: "Antes isso levava 14 dias. No último trimestre foram 5 dias. Até o fim do ano, a meta é 2 horas." },
    ],
    assistant: {
      title: "O que vamos gravar?",
      next: "Avançar",
      recording: "Gravando",
      pause: "Pausar",
      stop: "Parar",
      options: [
        { active: true, title: "Tela + microfone", desc: "Tela e voz" },
        { active: false, title: "Só microfone", desc: "Voz sem vídeo" },
      ],
    },
  },
  it: {
    reports: {
      add: "Aggiungi report IA",
      menu: ["Generale", "Riunioni", "Contenuti", "Presentazioni", "Ricerca", "Formazione", "IT e prodotto", "Vendite", "Recruiting"],
      cards: [
        { title: "Report riunione", desc: "Note formali della riunione" },
        { title: "Attività", desc: "Azioni e prossimi passi" },
      ],
    },
    chat: {
      assistant: "Assistente IA",
      firstQuestion: "Di cosa parla questo video? Qual è il tono?",
      firstAnswer: "Il video discute l'aggiornamento di un portale aziendale, il design, la struttura e il piano di lancio.",
      secondQuestion: "Come divideresti il video in sezioni?",
      secondAnswer: "La conversazione copre l'aggiornamento del portale, il design e la struttura...",
      input: "Scrivi...",
    },
    speakers: [
      { name: "Daniel", color: "rgba(227,134,116,0.4)", time: "00:10:42", text: "Propongo una nuova metrica. Dimentichiamo MQL e SQL. Misuriamo la velocità della fiducia: dal primo clic alla prova a pagamento." },
      { name: "Maya", color: "rgba(165,191,120,0.4)", time: "01:20:19", text: "Prima servivano 14 giorni. Nell'ultimo trimestre erano 5. Entro fine anno l'obiettivo è 2 ore." },
    ],
    assistant: {
      title: "Cosa registriamo?",
      next: "Avanti",
      recording: "Registrazione",
      pause: "Pausa",
      stop: "Stop",
      options: [
        { active: true, title: "Schermo + microfono", desc: "Schermo e voce" },
        { active: false, title: "Solo microfono", desc: "Voce senza video" },
      ],
    },
  },
  ru: {
    reports: {
      add: "Добавить ИИ-отчет",
      menu: ["Общее", "Для рабочих встреч", "Для создания контента", "Для презентаций", "Для исследований", "Для преподавателей", "Для IT, продакт-менеджмента", "Для продаж", "Для рекрутинга"],
      cards: [
        { title: "Отчет о встрече", desc: "Формальный отчет о встрече" },
        { title: "Задачи со встречи", desc: "Список задач и действий" },
      ],
    },
    chat: {
      assistant: "ИИ-ассистент",
      firstQuestion: "О чем видео? Какое настроение у участников?",
      firstAnswer: "Видео посвящено обсуждению обновления корпоративного портала, его дизайна, структуры и планов по внедрению новых модульных и динамических элементов.",
      secondQuestion: "На какие логические части ты поделишь видео?",
      secondAnswer: "Разговор посвящен обсуждению обновления корпоративного портала, его дизайна и структуры...",
      input: "Напишите...",
    },
    speakers: [
      { name: "Сергей", color: "rgba(227,134,116,0.4)", time: "00:10:42", text: "Я предлагаю новую метрику. Забудьте про MQL и SQL. Вводим показатель \"Скорость доверия\". Как его измерить? Просто. Время от первого клика до момента, когда клиент согласился оставить вам доступ к закрытому каналу или номер карты для оплаты пробного доступа." },
      { name: "Саша", color: "rgba(165,191,120,0.4)", time: "01:20:19", text: "Раньше это было 14 дней. В прошлом квартале — 5 дней. К концу года наша цель — 2 часа." },
    ],
    assistant: {
      title: "Что записываем?",
      next: "Далее",
      recording: "Идет запись",
      pause: "Пауза",
      stop: "Стоп",
      options: [
        { active: true, title: "Экран + микрофон", desc: "Запись экрана и голоса" },
        { active: false, title: "Только микрофон", desc: "Голос без видео" },
      ],
    },
  },
} as const;

const copy = illustrationCopy[illustrationLocale];
const scriptarioMobileScreenshot = {
  de: "/assets/illustrations/scriptario-mobile-de.jpg",
  en: "/assets/illustrations/scriptario-mobile-en.jpg",
  es: "/assets/illustrations/scriptario-mobile-es.jpg",
  "pt-br": "/assets/illustrations/scriptario-mobile-pt-br.jpg",
  it: "/assets/illustrations/scriptario-mobile-it.jpg",
  ru: "/assets/illustrations/scriptario-mobile-en.jpg",
}[illustrationLocale] || "/assets/illustrations/scriptario-mobile-en.jpg";

/** ИИ-отчеты: dropdown menu + report cards */
export function ReportsIllustration() {
  const menuItems = copy.reports.menu;
  const reports = copy.reports.cards;

  return (
    // `contents` on desktop (children stay absolute-to-card → no desktop change);
    // a sized relative box on max-desktop so the menus anchor below the flowed text.
    <div className="contents max-desktop:block max-desktop:relative max-desktop:h-[300px] max-tablet:h-[280px]">
      {/* "+ Добавить ИИ-отчет" button */}
      <div className="absolute left-[65px] top-[186px] max-desktop:left-[40px] max-desktop:top-[16px] max-tablet:left-[24px]">
        <div className="bg-[var(--brand-primary-tint)] border border-border-default flex gap-[8px] items-center justify-center px-[16px] py-[8px] rounded-[12px]">
          <span className="[font-family:var(--font-commissioner)] font-medium not-italic text-[12px] leading-[16px] text-text whitespace-nowrap">+</span>
          <span className="[font-family:var(--font-commissioner)] font-medium not-italic text-[12px] leading-[16px] text-text whitespace-nowrap">{copy.reports.add}</span>
        </div>
      </div>

      {/* Left panel: template menu */}
      <div className="absolute left-[65px] top-[226px] max-desktop:left-[40px] max-desktop:top-[56px] max-tablet:left-[24px] bg-white drop-shadow-[0px_0px_5px_rgba(0,0,0,0.1)] flex flex-col p-[4px] rounded-[12px] w-[240px] max-tablet:w-[150px]">
        {menuItems.map((item, i) => (
          <div key={item} className={`flex items-center justify-between p-[8px] rounded-[8px] w-full ${i === 0 ? "bg-black/[0.04]" : ""}`}>
            <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[12px] leading-[16px] text-text overflow-hidden text-ellipsis whitespace-nowrap tracking-[-0.24px]">{item}</span>
            <span className="size-[16px] text-gray-400 shrink-0">›</span>
          </div>
        ))}
      </div>

      {/* Right panel: report cards */}
      <div className="absolute left-[305px] top-[226px] max-desktop:left-[300px] max-desktop:top-[56px] max-tablet:left-[182px] bg-white drop-shadow-[0px_0px_5px_rgba(0,0,0,0.1)] flex flex-col p-[4px] rounded-[12px] w-[240px] max-tablet:w-[150px]">
        {reports.map((r) => (
          <div key={r.title} className="flex flex-col gap-[8px] items-start p-[8px] rounded-[8px] w-full">
            <div className="flex flex-col items-start shrink-0 whitespace-nowrap">
              <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[12px] leading-[16px] text-text overflow-hidden text-ellipsis tracking-[-0.24px]">{r.title}</span>
              <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[10px] leading-[16px] text-text-secondary overflow-hidden text-ellipsis tracking-[-0.2px]">{r.desc}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** ИИ чат: Telegram-style chat mockup */
export function ChatIllustration() {
  return (
    <div className="absolute left-[139px] -top-[240px] rotate-[3.76deg] max-desktop:!absolute max-desktop:!top-auto max-desktop:!bottom-[164px] max-desktop:!left-[24px] max-desktop:!right-[24px] max-desktop:!w-auto max-desktop:!rotate-0 max-desktop:!px-0 max-desktop:!flex max-desktop:!justify-center">
      <div className="bg-white drop-shadow-[0px_0px_5px_rgba(0,0,0,0.1)] flex flex-col justify-between pb-[16px] px-[16px] rounded-[24px] w-[400px] h-[520px] max-desktop:!w-full max-desktop:!max-w-none max-desktop:!h-[500px]">
        {/* Messages */}
        <div className="flex flex-col gap-[16px] items-end pt-[16px] w-full">
          {/* User message */}
          <div className="flex flex-col items-end w-full">
            <div className="bg-[var(--brand-primary-tint)] flex flex-col gap-[8px] items-start p-[12px] rounded-[16px] w-[233px]">
              <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-black">{copy.chat.firstQuestion}</span>
            </div>
          </div>
          {/* AI response */}
          <div className="flex flex-col items-start w-full">
            <div className="bg-[#F5F5F5] flex flex-col gap-[4px] items-start p-[12px] rounded-[16px] w-[268px]">
              <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-primary text-right whitespace-nowrap">{copy.chat.assistant}</span>
              <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-black">{copy.chat.firstAnswer}</span>
            </div>
          </div>
          {/* User message */}
          <div className="flex flex-col items-end w-full">
            <div className="bg-[var(--brand-primary-tint)] flex flex-col gap-[8px] items-start p-[12px] rounded-[16px] w-[249px]">
              <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-black">{copy.chat.secondQuestion}</span>
            </div>
          </div>
          {/* AI response */}
          <div className="flex flex-col items-start w-full">
            <div className="bg-[#F5F5F5] flex flex-col gap-[8px] items-start p-[12px] rounded-[16px] w-[268px]">
              <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-primary whitespace-nowrap">{copy.chat.assistant}</span>
              <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-black">{copy.chat.secondAnswer}</span>
            </div>
          </div>
        </div>
        {/* Input */}
        <div className="flex gap-[8px] items-start justify-center w-full">
          <div className="border border-border-default flex flex-1 gap-[8px] items-center min-w-0 px-[16px] py-[12px] rounded-[12px]">
            <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[12px] leading-[16px] text-text-secondary tracking-[-0.24px] whitespace-nowrap">{copy.chat.input}</span>
          </div>
          <div className="bg-primary flex gap-[8px] items-center justify-center p-[10px] rounded-[12px] shrink-0">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M2 10l16-8-8 16-2-6-6-2z" fill="white" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Разделение по спикерам: transcript cards */
export function SpeakersIllustration() {
  const speakers = copy.speakers;

  return (
    <div className="absolute left-[calc(50%+296px)] top-[60px] -translate-x-1/2 max-desktop:static max-desktop:translate-x-0 max-desktop:px-[40px] max-desktop:pb-[40px] max-tablet:px-[24px] max-tablet:pb-[24px] flex flex-col gap-[18px] max-tablet:gap-[12px] items-start w-[477px] max-desktop:w-full">
      {speakers.map((s) => (
        <div key={s.name} className="bg-white drop-shadow-[0px_0px_5px_rgba(0,0,0,0.1)] flex flex-col gap-[8px] items-end p-[20px] rounded-[16px] w-full">
          <div className="flex items-start justify-between w-full">
            <div className="flex gap-[8px] items-center shrink-0">
              <span className="bg-[{s.color}] px-[4px] py-[2px] rounded-[3px] text-[12px] leading-[16px] text-text tracking-[-0.24px] whitespace-nowrap" style={{ backgroundColor: s.color }}>{s.name}</span>
              <svg className="size-[16px] text-gray-400 shrink-0" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M227.31,73.37,182.63,28.68a16,16,0,0,0-22.63,0L36.69,152A15.86,15.86,0,0,0,32,163.31V208a16,16,0,0,0,16,16H92.69A15.86,15.86,0,0,0,104,219.31L227.31,96a16,16,0,0,0,0-22.63ZM92.69,208H48V163.31l88-88L180.69,120ZM192,108.68,147.31,64l24-24L216,84.68Z" /></svg>
              <svg className="size-[16px] text-gray-400 shrink-0" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M216,32H88a8,8,0,0,0-8,8V80H40a8,8,0,0,0-8,8V216a8,8,0,0,0,8,8H168a8,8,0,0,0,8-8V176h40a8,8,0,0,0,8-8V40A8,8,0,0,0,216,32ZM160,208H48V96H160Zm56-48H176V88a8,8,0,0,0-8-8H96V48H216Z" /></svg>
            </div>
            <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[16px] leading-[20px] text-text-secondary whitespace-nowrap">{s.time}</span>
          </div>
          <p className="[font-family:var(--font-commissioner)] font-normal not-italic text-[18px] leading-[24px] text-text w-full">{s.text}</p>
        </div>
      ))}
    </div>
  );
}

/** Ассистент встреч: recording UI + platform logos */
export function AssistantIllustration() {
  const options = copy.assistant.options;

  return (
    // `contents` on desktop (no change); on max-desktop a sized relative slot for dialogs + logos,
    // short on mobile where only logos show.
    <div className="contents max-desktop:block max-desktop:relative max-desktop:h-[340px] max-tablet:h-[80px]">
      {/* Main record dialog — right side */}
      <div className="absolute right-[129px] max-desktop:right-[-30px] top-[154px] max-desktop:top-[16px] rotate-[-2.09deg] max-tablet:hidden">
        <div className="bg-white drop-shadow-[0px_0px_8px_rgba(0,0,0,0.1)] flex flex-col gap-[24px] items-center p-[20px] rounded-[24px] w-[320px]">
          {/* Header */}
          <div className="flex items-center justify-between w-full">
            <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[12px] leading-[16px] text-text-secondary tracking-[-0.24px]">ivanpetrov@gmail.com</span>
          </div>
          <span className="[font-family:var(--font-commissioner)] font-medium not-italic text-[16px] leading-[20px] text-text">{copy.assistant.title}</span>
          {/* Options */}
          <div className="flex flex-col gap-[8px] items-start w-full">
            {options.map((opt) => (
              <div key={opt.title} className={`flex items-center justify-between p-[8px] rounded-[16px] w-full ${opt.active ? "bg-[var(--brand-primary-tint)] border border-[rgba(0,0,0,0.05)]" : "border border-[rgba(0,0,0,0.05)]"}`}>
                <div className="flex gap-[8px] items-center">
                  <div className={`flex items-center justify-center p-[8px] rounded-[12px] overflow-hidden shrink-0 ${opt.active ? "bg-white" : "bg-[var(--brand-primary-tint)]"}`}>
                    {opt.active ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="4" y="6" width="16" height="11" rx="2" stroke="var(--brand-primary)" strokeWidth="1.5"/><path d="M8 21h8" stroke="var(--brand-primary)" strokeWidth="1.5" strokeLinecap="round"/><circle cx="12" cy="13" r="1" fill="var(--brand-primary)"/></svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="8" y="4" width="8" height="12" rx="4" stroke="var(--brand-primary)" strokeWidth="1.5"/><path d="M6 10v2M18 10v2" stroke="var(--brand-primary)" strokeWidth="1.5" strokeLinecap="round"/><path d="M12 18v2" stroke="var(--brand-primary)" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    )}
                  </div>
                  <div className="flex flex-col gap-[4px] items-start">
                    <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-text">{opt.title}</span>
                    <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[12px] leading-[16px] text-text-secondary tracking-[-0.24px]">{opt.desc}</span>
                  </div>
                </div>
                <div className={`size-[20px] rounded-[10px] border flex items-center justify-center shrink-0 ${opt.active ? "border-[var(--brand-primary-light)] bg-white" : "border-border-default"}`}>
                  {opt.active && <div className="size-[10px] rounded-full bg-[var(--brand-primary-light)]" />}
                </div>
              </div>
            ))}
          </div>
          {/* CTA */}
          <div className="bg-primary flex gap-[8px] items-center justify-center px-[16px] py-[10px] rounded-[12px] w-full">
            <span className="[font-family:var(--font-commissioner)] font-medium not-italic text-[16px] leading-[20px] text-white whitespace-nowrap">{copy.assistant.next}</span>
          </div>
        </div>
      </div>

      {/* Recording indicator — top right */}
      <div className="absolute right-[-31px] top-[-14px] max-desktop:top-[-30px] max-desktop:right-[10px] rotate-[2.47deg] max-tablet:hidden">
        <div className="bg-white drop-shadow-[0px_0px_8px_rgba(0,0,0,0.1)] flex flex-col gap-[24px] items-center p-[20px] rounded-[24px] w-[320px]">
          <div className="flex items-center justify-between w-full">
            <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[12px] leading-[16px] text-text-secondary tracking-[-0.24px]">ivanpetrov@gmail.com</span>
          </div>
          <div className="flex flex-col gap-[8px] items-center py-[8px] w-full">
            <div className="flex gap-[8px] items-center justify-center">
              <div className="size-[16px] rounded-full bg-red-500" />
              <span className="[font-family:var(--font-commissioner)] font-medium not-italic text-[28px] leading-[32px] text-text tracking-[-0.56px] whitespace-nowrap">02:33</span>
            </div>
            <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[12px] leading-[16px] text-text-secondary tracking-[-0.24px] whitespace-nowrap">{copy.assistant.recording}</span>
          </div>
          <div className="flex gap-[8px] items-start w-full">
            <button className="border border-border-default flex flex-1 gap-[8px] items-center justify-center min-w-0 px-[16px] py-[10px] rounded-[12px]">
              <span className="[font-family:var(--font-commissioner)] font-medium not-italic text-[16px] leading-[20px] text-text whitespace-nowrap">{copy.assistant.pause}</span>
            </button>
            <button className="bg-[rgba(255,59,71,0.1)] border border-[#FF3B47] flex flex-1 gap-[8px] items-center justify-center min-w-0 px-[16px] py-[10px] rounded-[12px]">
              <span className="[font-family:var(--font-commissioner)] font-medium not-italic text-[16px] leading-[20px] text-[#FF3B47] whitespace-nowrap">{copy.assistant.stop}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mini recording pill — top right */}
      <div className="absolute right-[306px] max-desktop:right-[250px] top-[81px] max-desktop:top-[0px] rotate-[-8.43deg] max-tablet:hidden">
        <div className="bg-white drop-shadow-[0px_0px_8px_rgba(0,0,0,0.1)] flex flex-col gap-[8px] items-center p-[16px] rounded-[16px] w-[64px]">
          <div className="bg-[rgba(255,59,71,0.1)] flex gap-[8px] items-center justify-center p-[8px] rounded-[24px]">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="8" y="2" width="4" height="10" rx="2" stroke="#FF3B47" strokeWidth="1.5"/>
              <path d="M5 9a5 5 0 0 0 10 0" stroke="#FF3B47" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M10 14v3" stroke="#FF3B47" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[12px] leading-[16px] text-text tracking-[-0.24px] whitespace-nowrap">02:33</span>
        </div>
      </div>

      {/* Platform logos — Figma: left-[40px] top-[300px] gap-[24px] */}
      <div className="absolute left-[40px] max-tablet:left-[24px] top-[300px] max-desktop:top-auto max-desktop:bottom-[40px] max-tablet:bottom-[24px] flex gap-[24px] max-tablet:gap-[16px] items-center max-tablet:scale-[0.85] max-tablet:origin-left">
        {!isScriptario && <img src="/assets/illustrations/logo-yandex-telemost.png" alt="" className="size-[60px] shrink-0 object-contain" />}
        <img src="/assets/illustrations/logo-google-meet.png" alt="" className="h-[60px] w-[123px] shrink-0 object-contain" />
        <img src="/assets/illustrations/logo-zoom-wordmark.png" alt="" className="h-[30px] w-[134px] shrink-0 object-contain" />
      </div>
    </div>
  );
}

/** Market-specific companion surface: real Scriptario mobile UI or Transcripta Telegram mockup. */
export function BotIllustration() {
  if (isScriptario) {
    return (
      <div className="absolute left-0 top-[20px] max-desktop:left-1/2 max-desktop:-translate-x-1/2 w-[295px] h-[548px] flex items-start justify-center">
        <img
          src={scriptarioMobileScreenshot}
          alt=""
          className="h-[529px] w-[245px] rounded-[24px] border border-[#DDE7F6] object-cover object-top shadow-[0_18px_45px_rgba(15,23,42,0.16)]"
        />
      </div>
    );
  }

  return (
    <div className="absolute left-0 top-0 max-desktop:left-1/2 max-desktop:-translate-x-1/2 rotate-[4.77deg] w-[295px] h-[548px] flex items-center justify-center">
      <div className="relative h-[529px] w-[252px]">
        <img
          src="/assets/illustrations/phone-mockup.png"
          alt=""
          className="h-[529px] w-[252px] object-cover"
        />
        {/* Mask the staging chat-header title «(Stage) Транскрипта» — overlay matches the
            header bar colour and rotates with the mockup. Covers only the title, not the
            «‹ Benio» button, the right icon or the «бот» subtitle. */}
        <div className="absolute left-[64px] top-[60px] w-[130px] h-[15px] bg-[#26231e] rounded-[4px] flex items-center justify-center">
          <span className="[font-family:var(--font-commissioner)] font-medium not-italic text-[11px] leading-[14px] text-white whitespace-nowrap">
            {siteConfig.brandName}
          </span>
        </div>
      </div>
    </div>
  );
}

/** Транскрибация по ссылке, flag ON: one row of colour logos in white tiles
 *  with the name in a tooltip above. 36 px glyph in a 56 px tile on desktop and
 *  tablet, 22 px in 36 px below 769 px (six tiles = 256 px of the 263 px card
 *  content at 375 px). Desktop (≥1269): absolute bottom-left of the card;
 *  narrower: pinned to the bottom of the fixed-height card (slot is justify-end). */
const linkLogoRow =
  "absolute left-[40px] bottom-[40px] max-desktop:static max-desktop:left-auto max-desktop:bottom-auto max-desktop:px-[40px] max-desktop:pb-[40px]";

// Brand names are assembled at runtime: the Scriptario static verifier rejects
// literal Russian-service markers anywhere in the shared bundle.
const TRANSCRIPTA_LINK_SOURCES: SourceItem[] = [
  { id: "youtube", name: "YouTube" },
  { id: "rt", name: String.fromCharCode(82, 117, 84, 117, 98, 101) },
  { id: "vkv", name: `${String.fromCharCode(86, 75)} Видео` },
  { id: "ydisk", name: `${String.fromCharCode(1071)}ндекс Диск` },
  { id: "gdrive", name: "Google Диск" },
  { id: "zoom", name: "Zoom" },
];

const SCRIPTARIO_LINK_SOURCES: SourceItem[] = [
  { id: "youtube", name: "YouTube" },
  { id: "zoom", name: "Zoom" },
  { id: "gdrive", name: "Google Drive" },
];

/** Flag OFF (NEXT_PUBLIC_FEATURE_FILE_HOSTING_SOURCES unset): the pre-launch
 *  logos — YouTube on Scriptario; YouTube, RuTube, VK at 64/64/92 px otherwise. */
function LegacyLinkTranscribeIllustration({ ruTubeLogo }: { ruTubeLogo: string }) {
  if (isScriptario) {
    return (
      <div className="absolute left-[40px] bottom-[40px] max-desktop:static max-desktop:left-auto max-desktop:bottom-auto max-desktop:px-[40px] max-desktop:pb-[40px] max-tablet:px-[40px] max-tablet:pb-[40px] flex gap-[19px] items-end">
        <img src="/assets/illustrations/logo-youtube-scriptario.svg" alt="" className="size-[64px] shrink-0 object-contain" />
      </div>
    );
  }

  return (
    <div className="absolute left-[40px] bottom-[40px] max-desktop:static max-desktop:left-auto max-desktop:bottom-auto max-desktop:px-[40px] max-desktop:pb-[40px] max-tablet:px-[40px] max-tablet:pb-[40px] flex gap-[19px] items-end">
      <img src="/assets/illustrations/logo-youtube.png" alt="" className="size-[64px] shrink-0 object-contain" />
      <img src={ruTubeLogo} alt="" className="size-[64px] shrink-0 object-contain" />
      <img src="/assets/illustrations/logo-vk.png" alt="" className="h-[64px] w-[92px] shrink-0 object-contain" />
    </div>
  );
}

export function LinkTranscribeIllustration() {
  const ruTubeLogo = `/assets/illustrations/logo-${String.fromCharCode(114, 117, 116, 117, 98, 101)}.png`;

  if (!featureFileHostingSources) return <LegacyLinkTranscribeIllustration ruTubeLogo={ruTubeLogo} />;

  return (
    <div className={linkLogoRow}>
      <SourceLogoList
        items={isScriptario ? SCRIPTARIO_LINK_SOURCES : TRANSCRIPTA_LINK_SOURCES}
        label={landingContent.upload.supportedSources}
        glyph={36}
        glyphMobile={22}
        pad={10}
        padMobile={7}
        gap={12}
        gapMobile={8}
        surface="framed"
        tooltipSide="top"
      />
    </div>
  );
}
